import { defineStore } from 'pinia'
import { computed, ref } from 'vue'
import { meApi } from '@/api/me'
import type { AppOption } from '@/types/api'
import { tokenState } from '@/utils/token'
import { readStoredClientId, storeClientId } from './user'

const CLIENT_ID_STORAGE_KEY = 'uc.clientId'

/**
 * 控制台自身应用的识别码：兜底挑应用时优先按它命中。
 *
 * 不能拿列表首项当控制台：服务端按 `id DESC` 排序，首项是"最新创建的应用"，
 * 而刚建的应用通常一条菜单都还没有 —— 用它当控制台，整棵侧边导航会直接消失。
 * （应用列表 `GET /api/me/v1/me/apps` 的排序见 AppRepository#search）
 */
const CONSOLE_APP_CODES = ['ucenter']
const CONSOLE_CLIENT_IDS = ['uc-admin']

/** 兜底选应用：公认的控制台应用优先，否则取最早创建的（id 最小）—— 老应用更可能已配好菜单。 */
function pickFallbackClientId(list: AppOption[]): string | null {
  const consoleApp = list.find(
    (item) => CONSOLE_APP_CODES.includes(item.code) || CONSOLE_CLIENT_IDS.includes(item.clientId),
  )
  if (consoleApp !== undefined) {
    return consoleApp.clientId
  }
  const oldest = list.reduce<AppOption | null>(
    (min, item) => (min === null || Number(item.id) < Number(min.id) ? item : min),
    null,
  )
  return oldest?.clientId ?? null
}

/**
 * 应用上下文（附录C §2）。
 *
 * <p>这里有两个**性质不同**的应用标识，混用会直接产生用户可见的错乱：
 * <ul>
 *   <li>{@code consoleClientId} —— 控制台自身所属应用（登录时写进令牌的那个）。
 *       管理后台的侧边菜单与按钮权限**只认它**：导航结构属于控制台本身，
 *       不该因为"我此刻在管哪个业务应用"就被整个替换掉
 *       （替换掉的表现就是：选一下应用，左侧的工作台 / 应用管理 / 角色权限全变成另一个系统的菜单，
 *       用户会以为自己被踢到了别的系统）。</li>
 *   <li>{@code currentClientId} —— 当前**管理对象**。只决定 RBAC 页面读哪一份数据，
 *       以及菜单链接里 {@code {clientId}} 占位符填什么（点「菜单管理」进的就是它）。</li>
 * </ul>
 *
 * <p>两者都是 `client_id` 而不是 `uc_app.id`：后者是自增数字，
 * 出现在 URL / 请求头里就等于把"挨个试过去"的扫描入口直接暴露出去。
 *
 * <p>切换管理对象**不重签令牌**：令牌里的应用回答的是"我登录在哪个系统"，
 * 而 {@code /admin/v1/apps/{clientId}/**} 的鉴权由服务端按路径上的应用判定，与令牌无关。
 * 反之若重签，令牌里的登录应用就被改掉了，控制台菜单会跟着漂。
 */
export const useAppStore = defineStore('app', () => {
  const apps = ref<AppOption[]>([])
  const currentClientId = ref<string | null>(readStoredClientId())
  const consoleClientId = ref<string | null>(tokenState.clientId.value)
  /**
   * 控制台应用是否来自登录令牌（true）而不是从应用列表里兜底猜的（false）。
   * 猜出来的那个应用可能一条菜单都没有，空态文案必须能区分这两种情况，
   * 否则用户只会看到一片空白，无从判断是"没配菜单"还是"应用认错了"。
   */
  const consoleClientIdResolved = ref<boolean>(tokenState.clientId.value !== null)
  const loading = ref(false)

  const currentApp = computed(() =>
    apps.value.find((item) => item.clientId === currentClientId.value) ?? null)

  async function loadApps(): Promise<void> {
    loading.value = true
    try {
      apps.value = await meApi.apps()
      const preferred = currentClientId.value ?? tokenState.clientId.value
      const stillVisible = preferred !== null && apps.value.some((item) => item.clientId === preferred)
      setClientId(stillVisible ? preferred : pickFallbackClientId(apps.value))
      // 控制台应用只确定一次：此后无论管理对象怎么切，它都必须纹丝不动
      if (!consoleClientId.value) {
        const fromToken = tokenState.clientId.value
        const tokenVisible = fromToken !== null && apps.value.some((item) => item.clientId === fromToken)
        consoleClientId.value = tokenVisible ? fromToken : pickFallbackClientId(apps.value)
        consoleClientIdResolved.value = tokenVisible
      }
    } catch {
      apps.value = [] // 接口未就绪时由调用方自行降级
    } finally {
      loading.value = false
    }
  }

  function setClientId(clientId: string | null): void {
    currentClientId.value = clientId
    if (clientId) {
      storeClientId(clientId)
    } else {
      localStorage.removeItem(CLIENT_ID_STORAGE_KEY)
    }
  }

  return {
    apps,
    currentClientId,
    consoleClientId,
    consoleClientIdResolved,
    currentApp,
    loading,
    loadApps,
    setClientId,
  }
})
